import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import {
  ShieldCheck, Lock, Trash2, Star, ExternalLink, Plus,
  CreditCard, Banknote, Loader2, Check, AlertTriangle, Wallet,
} from "lucide-react";

// 15-min payout token kept in memory only (never localStorage)
let _payoutToken = "";
let _payoutTokenExp = 0;

const TYPES = [
  { id: "stripe_connect", label: "Stripe Connect", icon: CreditCard,
    blurb: "Best for global payouts. Onboard via Stripe (KYC-verified) and receive card, Apple Pay & Google Pay revenue automatically." },
  { id: "bank", label: "Bank account (ACH / SEPA)", icon: Banknote,
    blurb: "Direct deposit. Account + routing / IBAN required." },
];

function payoutHeaders() {
  return _payoutToken ? { "X-Payout-Token": _payoutToken } : {};
}

export default function PayoutSettings() {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(false);
  const [hasPassword, setHasPassword] = useState(true);
  const [password, setPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [unlockErr, setUnlockErr] = useState("");

  const [methods, setMethods] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null); // { needs_action, status, requirements, ... }
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null); // type id
  const [stripeBusy, setStripeBusy] = useState(false);
  const [toast, setToast] = useState("");

  const unlock = async (e) => {
    e?.preventDefault?.();
    setUnlocking(true); setUnlockErr("");
    try {
      const { data } = await api.post("/payouts/reauth", { password });
      _payoutToken = data.payout_token;
      _payoutTokenExp = Date.now() + (data.expires_in - 30) * 1000;
      setHasPassword(!!data.has_password);
      setUnlocked(true);
      setPassword("");
      await reload();
      // On return from Stripe onboarding, refresh status
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("connect") === "done") {
        await api.get("/payouts/stripe/status", { headers: payoutHeaders() }).catch(()=>{});
        await reload();
      }
    } catch (e) {
      setUnlockErr(formatErr(e));
    } finally { setUnlocking(false); }
  };

  const ensureToken = () => {
    if (!_payoutToken || Date.now() >= _payoutTokenExp) {
      setUnlocked(false);
      setToast("Session expired — re-authenticate.");
      throw new Error("expired");
    }
  };

  const reload = async () => {
    try {
      ensureToken();
      setLoading(true);
      const { data } = await api.get("/payouts/methods", { headers: payoutHeaders() });
      setMethods(data || []);
      // If Stripe Connect is attached, pull fresh status straight from Stripe so we
      // can accurately show "Complete My Stripe Profile" when info is missing/restricted.
      const hasStripe = (data || []).some((m) => m.type === "stripe_connect");
      if (hasStripe) {
        try {
          const { data: st } = await api.get("/payouts/stripe/status", { headers: payoutHeaders() });
          setStripeStatus(st);
        } catch { setStripeStatus(null); }
      } else {
        setStripeStatus(null);
      }
    } catch (e) {
      if (e.message !== "expired") setToast(formatErr(e));
    } finally { setLoading(false); }
  };

  const startStripeConnect = async () => {
    try {
      ensureToken();
      setStripeBusy(true);
      const origin_url = window.location.origin;
      const { data } = await api.post("/payouts/stripe/connect",
        { origin_url }, { headers: payoutHeaders() });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      setToast(formatErr(e));
    } finally { setStripeBusy(false); }
  };

  const setDefault = async (id) => {
    try {
      ensureToken();
      await api.post(`/payouts/methods/${id}/default`, {}, { headers: payoutHeaders() });
      await reload();
      setToast("Default payout updated.");
    } catch (e) { setToast(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this payout method? You'll need to add it again to use it.")) return;
    try {
      ensureToken();
      await api.delete(`/payouts/methods/${id}`, { headers: payoutHeaders() });
      await reload();
      setToast("Payout method removed.");
    } catch (e) { setToast(formatErr(e)); }
  };

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  if (!user) {
    return <div className="max-w-3xl mx-auto px-6 py-20"><p className="text-[#8A8A8A]">Please sign in.</p></div>;
  }

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto px-6 sm:px-8 py-14" data-testid="payouts-reauth">
        <div className="card p-8">
          <div className="h-12 w-12 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center mb-5">
            <Lock size={20}/>
          </div>
          <p className="overline mb-2">Secure step</p>
          <h1 className="h-display text-3xl mb-2">Confirm it&apos;s you</h1>
          <p className="text-sm text-[#4A4A4A] mb-6">
            Payout methods unlock a 15‑minute secure session. Sensitive details are encrypted at rest and never shown in full again.
          </p>
          <form onSubmit={unlock} className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e)=>setPassword(e.target.value)}
                className="input mt-2"
                placeholder="Your account password"
                autoComplete="current-password"
                data-testid="payouts-password-input"
              />
              <span className="text-xs text-[#8A8A8A] mt-2 inline-block">
                Google-only accounts: leave blank and confirm.
              </span>
            </label>
            {unlockErr && <p className="text-sm text-[#B84B4B]" data-testid="payouts-reauth-error">{unlockErr}</p>}
            <button className="btn-primary w-full" disabled={unlocking} data-testid="payouts-unlock-btn">
              {unlocking ? <><Loader2 size={16} className="animate-spin"/> Unlocking…</> : <><ShieldCheck size={16}/> Unlock payouts</>}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 py-10" data-testid="payouts-page">
      <p className="overline mb-3">Settings · Payouts</p>
      <h1 className="h-display text-4xl mb-2">Where your money lands.</h1>
      <p className="text-[#4A4A4A] max-w-2xl">
        Add one or more payout destinations. Sellers receive 100 % of their listed price — the 2 % Hoomanz Tip charged on top of each sale keeps this platform running.
      </p>

      <div className="mt-4 inline-flex items-center gap-2 text-xs text-[#4A4A4A]" data-testid="payouts-security-note">
        <ShieldCheck size={14} className="text-[#0A0A0A]"/>
        Encrypted at rest · Masked previews · Write‑only secrets · 15‑min secure session
      </div>

      <div className="mt-4 card p-4 bg-[#F2F2F0] border-[#E5E5E3]" data-testid="payouts-connect-hint">
        <p className="text-sm">
          <span className="font-medium">Want automatic seller-100 % / 2 %-tip splits?</span>{" "}
          <span className="text-[#4A4A4A]">
            Enable <a href="https://dashboard.stripe.com/connect" target="_blank" rel="noreferrer" className="underline">Stripe Connect</a> on the Hoomanz platform Stripe account, then click <em>Stripe Connect</em> below to onboard. Once payouts are enabled, every paid booking and product sale will automatically route 100 % of the seller&apos;s price to their Stripe account — the 2 % Hoomanz Tip added on top goes to the platform via Stripe&apos;s <code>application_fee_amount</code>.
          </span>
        </p>
      </div>

      {/* Existing methods */}
      <section className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium">Your payout methods</h2>
          {loading && <Loader2 size={14} className="animate-spin text-[#8A8A8A]"/>}
        </div>

        {methods.length === 0 && !loading && (
          <div className="card p-7 text-center" data-testid="payouts-empty">
            <p className="text-[#4A4A4A]">No payout methods yet. Add one below.</p>
          </div>
        )}

        <div className="grid gap-3" data-testid="payouts-list">
          {methods.map((m) => {
            const T = TYPES.find(t => t.id === m.type) || { label: m.type, icon: Wallet };
            const Icon = T.icon;
            const isStripe = m.type === "stripe_connect";
            // Prefer live status pulled straight from Stripe (has requirements + disabled_reason).
            const live = isStripe ? stripeStatus : null;
            const stripeReady = isStripe && (live ? (live.payouts_enabled && !live.needs_action) : m.payouts_enabled);
            const needsAction = isStripe && (live ? live.needs_action : !m.payouts_enabled);
            const isRestricted = isStripe && live?.status === "restricted";
            const reqCount = (live?.requirements?.currently_due?.length || 0) + (live?.requirements?.past_due?.length || 0);
            return (
              <div key={m.id} className="card p-5 flex flex-wrap items-center gap-4" data-testid={`payout-row-${m.id}`}>
                <div className="h-11 w-11 rounded-full bg-[#F2F2F0] flex items-center justify-center">
                  <Icon size={18}/>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{T.label}</p>
                    {m.is_default && (
                      <span className="badge badge-verified" data-testid={`payout-default-${m.id}`}>
                        <Star size={11}/> Default
                      </span>
                    )}
                    {isStripe && (
                      <span
                        className={
                          "text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 " +
                          (stripeReady
                            ? "bg-[#0A0A0A] text-white"
                            : isRestricted
                              ? "bg-[#B84B4B] text-white"
                              : "bg-[#F2F2F0] text-[#4A4A4A]")
                        }
                        data-testid={`payout-status-${m.id}`}>
                        {stripeReady && "Active"}
                        {!stripeReady && isRestricted && (<><AlertTriangle size={11}/> Restricted — action required</>)}
                        {!stripeReady && !isRestricted && needsAction && (<><AlertTriangle size={11}/> Info missing</>)}
                        {!stripeReady && !isRestricted && !needsAction && "Onboarding pending"}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#4A4A4A] mt-1 font-mono" data-testid={`payout-masked-${m.id}`}>
                    {m.masked || "•••• ••••"}
                  </p>
                  {isStripe && needsAction && (
                    <p className="text-xs text-[#B84B4B] mt-1" data-testid={`payout-status-detail-${m.id}`}>
                      {live?.requirements?.disabled_reason
                        ? `Stripe reason: ${live.requirements.disabled_reason.replace(/[._]/g, " ")}`
                        : reqCount > 0
                          ? `${reqCount} detail${reqCount === 1 ? "" : "s"} missing on your Stripe profile.`
                          : "Finish onboarding to start receiving payouts."}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isStripe && needsAction && (
                    <button onClick={startStripeConnect} disabled={stripeBusy}
                            className="btn-primary !py-2 !px-4 text-sm"
                            data-testid={`payout-complete-stripe-${m.id}`}>
                      {stripeBusy
                        ? <Loader2 size={14} className="animate-spin"/>
                        : <><ExternalLink size={14}/> Complete My Stripe Profile</>}
                    </button>
                  )}
                  {!m.is_default && (
                    <button onClick={()=>setDefault(m.id)} className="btn-ghost text-sm"
                            data-testid={`payout-make-default-${m.id}`}>
                      Make default
                    </button>
                  )}
                  <button onClick={()=>remove(m.id)} className="btn-ghost text-[#B84B4B]"
                          data-testid={`payout-remove-${m.id}`}>
                    <Trash2 size={16}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Add new method */}
      <section className="mt-10">
        <h2 className="text-lg font-medium mb-3">Add a payout method</h2>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="payouts-add-types">
          {TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => t.id === "stripe_connect" ? startStripeConnect() : setAdding(t.id)}
                className="card p-5 text-left hover:border-[#0A0A0A] transition-colors"
                data-testid={`payout-add-${t.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#F2F2F0] flex items-center justify-center"><Icon size={18}/></div>
                  <div>
                    <p className="font-medium">{t.label}</p>
                    <p className="text-xs text-[#4A4A4A] mt-0.5">{t.blurb}</p>
                  </div>
                  {t.id === "stripe_connect" && stripeBusy && <Loader2 size={14} className="ml-auto animate-spin"/>}
                  {t.id !== "stripe_connect" && <Plus size={16} className="ml-auto text-[#8A8A8A]"/>}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {adding && (
        <AddMethodModal
          type={adding}
          onClose={()=>setAdding(null)}
          onSaved={async ()=>{ setAdding(null); await reload(); setToast("Payout method saved (encrypted)."); }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#0A0A0A] text-white px-5 py-3 rounded-full shadow-lg text-sm inline-flex items-center gap-2"
             data-testid="payouts-toast">
          <Check size={14}/> {toast}
        </div>
      )}
    </div>
  );
}

function PayoutField({ label, name, type = "text", required = true, placeholder = "", form, setForm }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">{label}</span>
      <input
        type={type}
        required={required}
        autoComplete="off"
        spellCheck={false}
        className="input mt-1.5"
        placeholder={placeholder}
        value={form[name] || ""}
        onChange={(e)=>setForm({ ...form, [name]: e.target.value })}
        data-testid={`payout-field-${name}`}
      />
    </label>
  );
}

function AddMethodModal({ type, onClose, onSaved }) {
  const T = TYPES.find(t => t.id === type);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await api.post("/payouts/methods", { type, ...form }, { headers: payoutHeaders() });
      onSaved();
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="payouts-add-modal">
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()}
            className="bg-white rounded-3xl p-7 max-w-md w-full">
        <div className="mb-5">
          <p className="overline mb-1">Add payout method</p>
          <h3 className="font-heading text-2xl">{T?.label}</h3>
          <p className="text-xs text-[#4A4A4A] mt-1 inline-flex items-center gap-1">
            <Lock size={11}/> Encrypted on save. You&apos;ll only see a masked preview afterward.
          </p>
        </div>

        <div className="space-y-4">
          {type === "bank" && (
            <>
              <PayoutField label="Bank name" name="bank_name" placeholder="Chase, BoA, Wells Fargo…" form={form} setForm={setForm}/>
              <PayoutField label="Account holder" name="account_holder" placeholder="Full legal name" form={form} setForm={setForm}/>
              <PayoutField label="Account number" name="account_number" placeholder="•••• •••• ••••" form={form} setForm={setForm}/>
              <PayoutField label="Routing number (US) / Sort code" name="routing_number" placeholder="9 digits" form={form} setForm={setForm}/>
              <div className="grid grid-cols-2 gap-3">
                <PayoutField label="SWIFT/BIC (intl)" name="swift" required={false} placeholder="optional" form={form} setForm={setForm}/>
                <PayoutField label="Country" name="country" required={false} placeholder="US" form={form} setForm={setForm}/>
              </div>
              <PayoutField label="IBAN (intl)" name="iban" required={false} placeholder="optional" form={form} setForm={setForm}/>
            </>
          )}
        </div>

        {err && <p className="text-sm text-[#B84B4B] mt-4" data-testid="payouts-add-error">{err}</p>}

        <div className="flex gap-2 mt-6">
          <button type="button" onClick={onClose} className="btn-ghost flex-1" data-testid="payouts-add-cancel">Cancel</button>
          <button className="btn-primary flex-1" disabled={saving} data-testid="payouts-add-save">
            {saving ? <><Loader2 size={14} className="animate-spin"/> Encrypting…</> : <><ShieldCheck size={14}/> Save securely</>}
          </button>
        </div>
      </form>
    </div>
  );
}
